import { mapValues, cloneDeep, uniq } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";
import { i18n } from "@/i18n";
import { pickBy, filter } from "@/helpers/utility/CollectionFunctions";

import GraphModel from "@editor/items/relation/GraphModel";

import { showConfirm } from "@core/ui/alert/alert";
import { useWayStore } from "@editor/items/way/ways.store";
import { useElements } from "../element/elements";
import { getEndItem, getStartItem } from "./relationFunctions";

import type {
  RelationItem,
  CurveFormType,
  RelationUpdateType,
  RelationStartItemType,
  RelationEndItemType
} from "./RelationItem";
import type { GraphPoint } from "./GraphPoint";
import type { ExpressionValue } from "../expression/expressionFunctions";
import { useSceneRelationSettingsStore } from "./sceneRelationSettings.store";
import { toUid, type Uid } from "@/helpers/utility/Id";

export const relationStructure: RelationItem = {
  type: "relation",
  id: 0,
  uid: "relation_0",

  title: i18n.t("objectNames.impact"),
  description: "",

  startItemId: -1,
  startItemType: "target",

  endItemId: -1,
  endItemType: "target",

  maxInfluence: 0,

  // curve:
  inverse: false,

  curveType: "linear",
  curvePoints: [],

  /**
   * Set when the relation is triggered
   * change: startItem value changed
   * none: never
   */
  updateType: "change",

  formula: "1 * x", // only used when curveType is formula

  delay: 0,

  docs: {},

  createdAt: -1
};

export const useRelationStore = defineItemStore(
  "model/relations",
  relationStructure,
  {
    state: () => ({
      roundPaths: true,
      spacePaths: true,
      deoverlapPaths: true,

      /**
       * Temporary store of relation information that is used during creation.
       * RelationDraft Id is built like `startItemType.startItemId-endItemType.endItemId`
       */
      relationDrafts: {} as {
        [relationDraftId: string]: {
          description: string;
          updateType: "change" | "none";
          transmissionType: string;
        };
      }
    }),

    getters: {
      /**
       * Get only the relations that are not "NOT-Relations"
       */
      active(state): { [relationId: number]: RelationItem } {
        // filter out all "none" relations
        return pickBy(state.items, (relation) => relation.updateType != "none");
      },
      activeIds(): number[] {
        return Object.values(this.active).map((r: RelationItem) => r.id);
      },
      activeByEndUid(): Record<Uid<"target" | "resource" | "index">, RelationItem[]> {
        const result: Record<Uid<"target" | "resource" | "index">, RelationItem[]> = {};

        for (const relation of this.orderedItems) {
          if (relation.updateType == "none") continue;
          const uid = toUid(relation.endItemType, relation.endItemId);
          if (result[uid] == null) {
            result[uid] = [];
          }
          result[uid].push(relation);
        }

        return result;
      },
      activeByStartUid(): Record<Uid<"target" | "resource" | "index">, RelationItem[]> {
        const result: Record<Uid<"target" | "resource" | "index">, RelationItem[]> = {};

        for (const relation of this.orderedItems) {
          if (relation.updateType == "none") continue;
          const uid = toUid(relation.startItemType, relation.startItemId);
          if (result[uid] == null) {
            result[uid] = [];
          }
          result[uid].push(relation);
        }

        return result;
      },
      byStartUid(state): Record<Uid<"target" | "resource" | "index">, RelationItem[]> {
        const result: Record<Uid<"target" | "resource" | "index">, RelationItem[]> = {};

        for (const relation of this.orderedItems) {
          const uid = toUid(relation.startItemType, relation.startItemId);
          if (result[uid] == null) {
            result[uid] = [];
          }
          result[uid].push(relation);
        }

        return result;
      },
      byEndUid(state): Record<Uid<"target" | "resource" | "index">, RelationItem[]> {
        const result: Record<Uid<"target" | "resource" | "index">, RelationItem[]> = {};

        for (const relation of this.orderedItems) {
          const uid = toUid(relation.endItemType, relation.endItemId);
          if (result[uid] == null) {
            result[uid] = [];
          }
          result[uid].push(relation);
        }

        return result;
      },

      /**
       * Strength is a three-tier categorization of the max influence of the relation
       * used to show line width.
       */
      strengths(): { [relationId: string]: "weak" | "normal" | "strong" } {
        // calculation based on --INFLUENCE--
        const baseInfluences = this.maxInfluences;

        const targetRelationInfluences = filter(
          this.maxInfluences,
          (maxInfluence, relationId) =>
            this.byId[relationId].startItemType == "target" &&
            this.byId[relationId].endItemType == "target"
        );

        /// alternative: calculation based on --SLOPE--
        // const baseInfluences = state.curveSlopes;

        // get the highest max influence and the lowest max influence
        const absoluteInfluences = targetRelationInfluences.map((s) =>
          Math.abs(s)
        );
        const influenceValues = absoluteInfluences.filter((i) => i != 0);
        const highestInfluence = Math.max(...influenceValues);
        const lowestInfluence = Math.min(...influenceValues);

        // assign all relations a rating based on their value
        // relative to the others:
        const step = (highestInfluence - lowestInfluence) / 3;
        return mapValues(baseInfluences, (influence, relationId) => {
          // don't include non-target-relations in strength calculations
          if (
            this.byId[relationId].startItemType != "target" ||
            this.byId[relationId].endItemType != "target"
          ) {
            return "normal";
          }

          if (influence == 0) return "normal";

          if (lowestInfluence === highestInfluence) return "normal";
          if (influence <= lowestInfluence + step) return "weak";
          if (influence <= highestInfluence - step) return "normal";
          return "strong";
        });
      },

      maxInfluences(): { [relationId: string]: number } {
        return mapValues(
          this.graphModels,
          (model) => model.absoluteMaxInfluence
        );
      },

      curveSlopes(): { [relationId: number]: number } {
        const elementValues = useElements().elementValues;
        const active: { [relationId: number]: RelationItem } = this.active;
        const graphModels: { [relationId: number]: GraphModel } =
          this.graphModels;

        return getCurveSlopesForStartItemValues(
          active,
          graphModels,
          elementValues
        );
      },

      /**
       * returns the slope of the curve of the graphModel at a certain point
       */
      getCurveSlopesForStartItemValues() {
        return (elementValues: {
          [uid: string]: ExpressionValue;
        }): { [relationId: number]: number } => {
          return mapValues(this.active, (relation, relationId) => {
            const model = this.graphModels[relationId];
            
            if (elementValues == null) {
              console.warn("elementValues are null");
            }

            const startItemUid = relation.startItemType + "_" + relation.startItemId;
            const startItemValue = Number(elementValues[startItemUid]?.value ?? 0);

            const derivative = model.derivativeAtX(startItemValue, true);

            // Cap the slope at the distance between min and max value
            // because relations can't change the value of the target
            // item by more than it's value range.
            const endItem = getEndItem(relation);
            const endItemValue = elementValues[endItem.uid];
            const { minValue, maxValue } = endItemValue;
            if (minValue != null && maxValue != null) {
              const distance = Math.abs(maxValue - minValue);
              if (Math.abs(derivative) > distance) {
                return derivative > 0 ? distance : -distance;
              }
            }

            return derivative;
          });
        };
      },

      graphModels(): { [relationId: string]: GraphModel } {
        return mapValues(this.active, (relation) => new GraphModel(relation.curvePoints));
      },

      valueAt(): (id: number, x: number) => number {
        return (id: number, x: number) => this.graphModels[id].valueAtX(x);
      },
      getBetweenItems() {
        return (
          startItem: { id: number; type: RelationStartItemType },
          endItem: { id: number; type: RelationEndItemType }
        ): RelationItem | undefined =>
          this.orderedItems.find(
            (relation) =>
              relation.startItemId === startItem.id &&
              relation.startItemType === startItem.type &&
              relation.endItemId === endItem.id &&
              relation.endItemType === endItem.type
          );
      },

      getStartItem() {
        return (id: number) => {
          const relation = this.byId[id];
          return getStartItem(relation);
        };
      },

      getEndItem() {
        return (id: number) => {
          const relation = this.byId[id];
          return getEndItem(relation);
        };
      },

      getByStartItem() {
        return (item: { id: number; type: "target" | "resource" }) => {
          return this.orderedItems.filter(
            (relation) =>
              relation.updateType != "none" &&
              relation.startItemId == item.id &&
              relation.startItemType == item.type
          );
        };
      },

      expressionValues(): { [uid: string]: ExpressionValue } {
        const elementValues = useElements().elementValues;
        const graphModels = this.graphModels;
        return mapValues(this.byUid, (relation) => {
          const startItem = getStartItem(relation);

          const startItemUid = startItem?.type + "_" + startItem?.id;
          const startItemExpressionValue = elementValues[startItemUid];
          const graphModel = graphModels[relation.id];
          if (!startItemExpressionValue || !graphModel) {
            return {
              value: NaN,
              minValue: 0,
              maxValue: 0,
              unit: "",
              unitValid: true,
              valid: false
            };
          }
          
          const value = graphModel.valueAtX(Number(startItemExpressionValue.value));
          const minValue = startItemExpressionValue.minValue == null ? null : graphModel.valueAtX(startItemExpressionValue.minValue);
          const maxValue = startItemExpressionValue.maxValue == null ? null : graphModel.valueAtX(startItemExpressionValue.maxValue);
          const unit = startItemExpressionValue.unit;
          const unitValid = startItemExpressionValue.unitValid;

          return {
            value,
            minValue: (minValue == null || maxValue == null) ? null : Math.min(minValue, maxValue),
            maxValue: (minValue == null || maxValue == null) ? null : Math.max(minValue, maxValue),
            unit,
            unitValid,
            valid: true
          };
        });
      }
    },

    events: {
      async beforeCreate(data) {
        if (data.startItemId == null || data.startItemType == null) {
          throw new Error("can't create relation, item type missing");
        }
        const startElementUid = toUid(data.startItemType, data.startItemId);

        const values = useElements().elementValues;
        const startItemValue = values[startElementUid];
        let minValue = null;
        let maxValue = null;
        if (startItemValue) {
          minValue = startItemValue.minValue;
          maxValue = startItemValue.maxValue;
        } else {
          console.warn(`no value found for start item with uid ${startElementUid}`);
        }

        const MAX_SAFE_VALUE = 1_000_000_000_000_000; // 1 quadrillion

        // TODO: improve this:
        // if the item has no value range, set it to 0-100
        if (minValue == null) minValue = 0;
        if (maxValue == null) maxValue = 100;

        if (maxValue > MAX_SAFE_VALUE) maxValue = MAX_SAFE_VALUE;
        if (minValue < -MAX_SAFE_VALUE) minValue = -MAX_SAFE_VALUE;

        const range = maxValue - minValue;

        if (range > 5000) {
          data.formula = "0 * x";
          data.curveType = "formula";
        }

        if (minValue == maxValue) {
          data.curvePoints = [
            {
              id: 0,
              x: minValue,
              y: 0
            }
          ];
        } else {
          data.curvePoints = [
            {
              id: 0,
              x: minValue,
              y: 0,
              bezierOut: {
                x: 0,
                y: 0
              }
            },
            {
              id: 1,
              x: maxValue,
              y: 0,
              bezierIn: {
                x: 0,
                y: 0
              }
            }
          ];
        }

        return data;
      },
      async created(relation, store) {
        // delete the relation draft as soon as the relation is created
        const relationDraftId = `${relation.startItemType}.${relation.startItemId}-${relation.endItemType}.${relation.endItemId}`;
        if (store.relationDrafts?.[relationDraftId]) {
          delete store.relationDrafts[relationDraftId];
        }

        // newly created relations should be added to the front of the order, not the back
        store.order = uniq([relation.id, ...store.order]);
      },
      beforeUpdate(relation, changedData, oldRelation) {
        const changes: Partial<RelationItem> = {};

        if ("maxInfluence" in changedData) {
          // update the inverse flag if maxInfluence changed
          const maxInfluence = relation.maxInfluence;

          const curvePoints = relation.curvePoints;

          // update the curve of the relation:
          // the extreme point should be updated to the new max.
          const oldGraphModel = new GraphModel(curvePoints);
          const extremePoints = oldGraphModel.extremePoints;
          const defaultDir = oldRelation.inverse ? -1 : 1;
          for (const extremePoint of extremePoints) {
            const extremePointIndex = curvePoints.findIndex(
              (p) => p.x === extremePoint.x
            );
            // don't move the first point
            if (extremePointIndex === 0) continue;
            const newExtremePoint = curvePoints[extremePointIndex];
            newExtremePoint.y =
              (Math.sign(newExtremePoint.y) || defaultDir) * maxInfluence;
          }

          // save if the relation is inverse
          const graphModel = new GraphModel(curvePoints);

          // if the curve actually influences anything, get the inverse setting from the curve.
          if (graphModel.absoluteMaxInfluence > 0) {
            changes.inverse = graphModel.inverted;
          }

          changes.curvePoints = curvePoints;
        }

        return changes;
      },
      async canBeDeleted(relation) {
        // don't ask any questions if one of the corresponding items has been deleted.
        const startItem = getStartItem(relation);
        const endItem = getEndItem(relation);
        if (startItem == null || endItem == null) {
          return true;
        }

        return await showConfirm(
          i18n.t("questions.deleteRelation"),
          i18n.t("actions.deleteRelation"),
          i18n.t("labels.abort")
        );
      },
      async afterDelete(relation) {
        // remove relation from ways it is included in
        const ways = useWayStore().orderedItems.filter((way) =>
          way.relationIds.includes(relation.id)
        );
        for (const way of ways) {
          while (way.relationIds.includes(relation.id)) {
            useWayStore().removeLastRelation({ id: way.id });
          }
        }

        // Remove scene-specific settings for this relation
        const sceneSettingsStore = useSceneRelationSettingsStore();
        const settings = sceneSettingsStore.orderedItems.filter((settings) => settings.relationId === relation.id);
        for (const setting of settings) {
          await sceneSettingsStore.delete({ id: setting.id, instant: true });
        }
      }
    },

    actions: {
      /**
       */
      updateCurve(options: {
        id: number;
        curveType: CurveFormType;
        curvePoints: GraphPoint[];
      }) {
        const { id, curveType, curvePoints } = options;
        const relation = this.items[id];
        const updateType = relation.updateType;

        if (updateType == "none") {
          console.warn("can't update 'none' curve.");
          return;
        }

        // we seperate the update to the curve in two sets.

        // set 1: curve data (points, factor and type)
        const dataToChange = {
          id,
          curvePoints: cloneDeep(curvePoints),
          curveType: relation.curveType
        };

        if (relation.curveType !== curveType) {
          dataToChange.curveType = curveType;
        }

        this.update(dataToChange);

        // set 2: metadata (inverse and maxinfluence)
        const oldMaxInfluence = this.items[id]?.maxInfluence;
        const newData = {
          id,
          inverse: this.graphModels[id].maxInfluence < 0,
          maxInfluence: oldMaxInfluence
        };

        const newMaxInfluence = this.maxInfluences[id];

        if (oldMaxInfluence !== newMaxInfluence) {
          newData.maxInfluence = newMaxInfluence;
        }

        this.update(newData);
      },

      /**
       * Scale the curve down/up according to maxInfluence.
       * @param relation
       */
      updatePointBounds({ id }: { id: number }) {
        const relation = this.items[id];
        const points = relation.curvePoints;
        for (const point of points) {
          if (point.y > relation.maxInfluence) {
            point.y = relation.maxInfluence;
          } else if (point.y < -relation.maxInfluence) {
            point.y = -relation.maxInfluence;
          }
        }
      },
      invertCurve({ id }: { id: number }) {
        const relation = this.items[id];
        
        if (relation.updateType == "none") {
          // force transmission; curve inverting is senseless without transmission.
          relation.updateType = "change";
        }
        
        const graphModel = this.graphModels[id];
        relation.curvePoints = graphModel.inverse();

        if (relation.curveType === "formula") {
          const formula = relation.formula.trim();
          if (relation.formula.startsWith("-(") && formula.endsWith(")")) {
            relation.formula = formula.slice(2, -1);
          } else {
            relation.formula = `-(${formula})`;
          }
        }

        if (graphModel.absoluteMaxInfluence > 0) {
          relation.inverse = !graphModel.inverted;
        } else {
          relation.inverse = !relation.inverse;
        }
      },

      /**
       * Delete connections that do not exist anymore, add new connections.
       *
       * @param items All items (targets or resources) to connect
       * @param options
       * @param options.endItemId The id of the item whose connection should be updated
       * @param options.endItemType The type of the item whose connection should be updated
       */
      async updateIncomingConnections(
        items: { id: number; type: "target" | "resource" | "index" }[],
        options: {
          endItemId: number;
          endItemType: "target" | "resource";
          updateType?: RelationUpdateType;
        }
      ) {
        const { endItemId, endItemType } = options;

        if (endItemId == null || endItemType == null) {
          throw new Error("Id and type of relation end must be specified.");
        }

        // remove all connections to targets that are not connected anymore.
        const relations: RelationItem[] = this.orderedItems.filter(
          (relation) =>
            relation.endItemType === endItemType &&
            relation.endItemId === endItemId
        );
        const removedRelations = relations.filter(
          (relation) =>
            items.find(
              (item) =>
                item.id === relation.startItemId &&
                item.type === relation.startItemType
            ) == null
        );
        // disconnect targets
        await this.deleteMany(removedRelations);

        // collect all the items that are connected by relations
        const connectedItems = relations.map((relation) => ({
          id: relation.startItemId,
          type: relation.startItemType
        }));
        // connect all targets that are not currently connected
        const itemsToConnect = items.filter(
          (item) =>
            connectedItems.find(
              (connectedItem) =>
                item.id === connectedItem.id && item.type === connectedItem.type
            ) == null
        );
        return this.createManyIncoming(itemsToConnect, options);
      },

      /**
       * Delete connections that do not exist anymore, add new connections.
       *
       * @param items All items (targets or resources) to connect
       * @param options
       * @param options.startItemId The id of the item whose connection should be updated
       * @param options.startItemType The type of the item whose connection should be updated
       */
      async updateOutgoingConnections(
        items: { id: number; type: "target" | "resource" | "index" }[],
        options: {
          startItemId: number;
          startItemType: "target" | "resource" | "index";
          updateType?: RelationUpdateType;
        }
      ) {
        const { startItemId, startItemType } = options;

        if (startItemId == null || startItemType == null) {
          throw new Error("Id and type of relation end must be specified.");
        }

        // remove all connections to items that are not connected anymore.
        const relations: RelationItem[] = this.orderedItems.filter(
          (relation) =>
            relation.startItemType === startItemType &&
            relation.startItemId === startItemId
        );
        const removedRelations = relations.filter(
          (relation) =>
            items.find(
              (item) =>
                item.id === relation.endItemId &&
                item.type === relation.endItemType
            ) == null
        );
        // disconnect items
        await this.deleteMany(removedRelations);

        // collect all the items that are connected by relations
        const connectedItems = relations.map((relation) => ({
          id: relation.endItemId,
          type: relation.endItemType
        }));
        // connect all items that are not currently connected
        const itemsToConnect = items.filter(
          (item) =>
            connectedItems.find(
              (connectedItem) =>
                item.id === connectedItem.id && item.type === connectedItem.type
            ) == null
        );
        return this.createManyOutgoing(itemsToConnect, options);
      },

      /**
       * Create many new relations by connecting any amount of items to an end item.
       *
       * @param items All items that should be connected
       * @param options
       * @param options.endItemId The id of the item the targets should be connected to
       * @param options.endItemType The type of the item the targets should be connected to
       */
      createManyIncoming(
        items: { id: number; type: "target" | "resource" }[],
        options: { endItemId: number; endItemType: "target" | "resource" }
      ) {
        const newRelationsData = items.map((item) => {
          return {
            ...options,
            startItemId: item.id,
            startItemType: item.type
          };
        });
        return this.createMany(newRelationsData);
      },

      /**
       * Create many outgoing relations
       */
      createManyOutgoing(
        items: { id: number; type: "target" | "resource" }[],
        options: { startItemId: number; startItemType: "target" | "resource" }
      ) {
        const newRelationsData = items.map((item) => {
          return {
            ...options,
            endItemId: item.id,
            endItemType: item.type
          };
        });
        return this.createMany(newRelationsData);
      }

      // // Unused for now:
      // /**
      //  * Change the max influence of all relations that influence
      //  * one target, according to the max influence of the target
      //  * @param {*} target The target that is beeing influenced
      //  */
      // updateInfluences(target) {
      //   // get all the relations that are influencing that target
      //   const relations = this.activeByEndTarget[target.id];

      //   // get the amount that the relations influence now
      //   const totalBefore = relations.reduce(
      //     (sum, relation) => sum + relation.maxInfluence,
      //     0
      //   );
      //   // change the influence of each relation
      //   for (const relation of relations) {
      //     let influenceProportion;
      //     if (totalBefore !== 0) {
      //       influenceProportion = relation.maxInfluence / totalBefore;
      //     } else {
      //       influenceProportion = 0;
      //     }

      //     const newInfluence = Math.floor(
      //       target.maxInfluence * influenceProportion
      //     );
      //     this.update({
      //       id: relation.id,
      //       maxInfluence: newInfluence
      //     });
      //     this.updatePointBounds({
      //       id: relation.id
      //     });
      //   }
      //   return relations;
      // },
    }
  }
);

function getCurveSlopesForStartItemValues(
  activeRelations: { [relationId: number]: RelationItem },
  graphModels: { [relationId: number]: GraphModel },
  elementValues: { [uid: string]: ExpressionValue }
): { [relationId: number]: number } {
  return mapValues(activeRelations, (relation, relationId) => {
    const model = graphModels[parseInt(relationId)];

    const startItemUid = relation.startItemType + "_" + relation.startItemId;
    const startItemValue = Number(elementValues[startItemUid]?.value || 0);

    const derivative = model.derivativeAtX(startItemValue, true);

    // Cap the slope at the distance between min and max value
    // because relations can't change the value of the target
    // item by more than it's value range.
    const endItem = getEndItem(relation);
    const endItemValue = elementValues[endItem.uid];
    if (!endItemValue) {
      throw new Error("endItemValue is null");
    }
    const { minValue, maxValue } = endItemValue;
    if (minValue != null && maxValue != null) {
      const distance = Math.abs(maxValue - minValue);
      if (Math.abs(derivative) > distance) {
        return derivative > 0 ? distance : -distance;
      }
    }

    return derivative;
  });
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useRelationStore, import.meta.hot));
}
